import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Edit, Trash2, Copy } from "lucide-react";
import { z } from "zod";

const promptSchema = z.object({
  use_case: z.string().trim().min(1, "Use case is required").max(100, "Use case must be less than 100 characters"),
  tool: z.string().trim().min(1, "Tool is required").max(50, "Tool must be less than 50 characters"),
  description: z.string().max(500, "Description must be less than 500 characters").optional(),
  prompt_text: z.string().trim().min(10, "Prompt must be at least 10 characters").max(5000, "Prompt must be less than 5000 characters")
});

interface Prompt {
  id: string;
  use_case: string;
  prompt_text: string;
  tool: string;
  description?: string;
  created_at: string;
}

const isDev = import.meta.env.DEV;

export const PromptsManager = () => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    use_case: "",
    prompt_text: "",
    tool: "",
    description: ""
  });

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const { data, error } = await supabase
        .from("prompts")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        if (isDev) console.error("Error loading prompts:", error);
        if (error.message?.includes("Failed to fetch")) {
          setError("Unable to connect to the server. Please check your connection and try again.");
        } else {
          setError(error.message);
        }
        return;
      }
      
      // Successfully loaded data - clear any errors
      setPrompts(data || []);
      setError(null);
    } catch (error) {
      if (isDev) console.error("Error loading prompts:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validate input data
      const validatedData = promptSchema.parse(formData);
      
      if (editingPrompt) {
        const { error } = await supabase
          .from("prompts")
          .update({
            use_case: validatedData.use_case,
            tool: validatedData.tool,
            description: validatedData.description || null,
            prompt_text: validatedData.prompt_text
          })
          .eq("id", editingPrompt.id);

        if (error) throw error;
        toast({ title: "Success", description: "Prompt updated successfully" });
      } else {
        const { error } = await supabase
          .from("prompts")
          .insert([{
            use_case: validatedData.use_case,
            tool: validatedData.tool,
            description: validatedData.description || null,
            prompt_text: validatedData.prompt_text
          }]);

        if (error) throw error;
        toast({ title: "Success", description: "Prompt created successfully" });
      }

      setIsDialogOpen(false);
      setEditingPrompt(null);
      setFormData({ use_case: "", prompt_text: "", tool: "", description: "" });
      loadPrompts();
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        if (isDev) console.error("Error saving prompt:", error);
        toast({
          title: "Error",
          description: "Failed to save prompt",
          variant: "destructive",
        });
      }
    }
  };

  const handleEdit = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      use_case: prompt.use_case,
      prompt_text: prompt.prompt_text,
      tool: prompt.tool,
      description: prompt.description || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this prompt?")) return;

    try {
      const { error } = await supabase
        .from("prompts")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast({ title: "Success", description: "Prompt deleted successfully" });
      loadPrompts();
    } catch (error) {
      if (isDev) console.error("Error deleting prompt:", error);
      toast({
        title: "Error",
        description: "Failed to delete prompt",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied!", description: "Prompt copied to clipboard" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy prompt",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div>Loading prompts...</div>;
  }

  if (error) {
    return (
      <Card className="p-8 text-center">
        <h3 className="text-lg font-semibold mb-2 text-destructive">Connection Error</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={loadPrompts}>Retry</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">AI Prompts</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              setEditingPrompt(null);
              setFormData({ use_case: "", prompt_text: "", tool: "", description: "" });
            }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Prompt
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingPrompt ? "Edit Prompt" : "Add New Prompt"}</DialogTitle>
              <DialogDescription>
                Create or edit an AI prompt for your collection.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="use_case">Use Case</Label>
                <Input
                  id="use_case"
                  value={formData.use_case}
                  onChange={(e) => setFormData({ ...formData, use_case: e.target.value })}
                  placeholder="e.g., Content Creation, Email Marketing..."
                  required
                />
              </div>
              <div>
                <Label htmlFor="tool">Tool</Label>
                <Input
                  id="tool"
                  value={formData.tool}
                  onChange={(e) => setFormData({ ...formData, tool: e.target.value })}
                  placeholder="e.g., ChatGPT, Claude, Gemini..."
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of what this prompt does..."
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="prompt_text">Prompt Text</Label>
                <Textarea
                  id="prompt_text"
                  value={formData.prompt_text}
                  onChange={(e) => setFormData({ ...formData, prompt_text: e.target.value })}
                  placeholder="Enter the full prompt here..."
                  rows={6}
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingPrompt ? "Update" : "Create"} Prompt
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 overflow-x-auto">
        {prompts.map((prompt) => (
          <Card key={prompt.id}>
            <CardHeader>
              <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary">{prompt.use_case}</Badge>
                    <Badge variant="outline">{prompt.tool}</Badge>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(prompt.prompt_text)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(prompt)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(prompt.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {prompt.description && (
                <div className="mt-3">
                  <p className="text-sm text-muted-foreground italic">
                    {prompt.description}
                  </p>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm font-mono leading-relaxed whitespace-pre-wrap">
                  {prompt.prompt_text}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {prompts.length === 0 && (
        <Card className="p-8 text-center">
          <h3 className="text-lg font-semibold mb-2">No prompts yet</h3>
          <p className="text-muted-foreground mb-4">
            Start building your prompt collection by adding your first prompt.
          </p>
        </Card>
      )}
    </div>
  );
};